import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, ExternalLink, Newspaper, Users } from "lucide-react";

export default function News() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("league") ? "squad" : "global");
  const [leagueId, setLeagueId] = useState(params.get("league") || "");
  const [leagues, setLeagues] = useState([]);
  const [global, setGlobal] = useState(null);
  const [squad, setSquad] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/leagues").then(r => {
      setLeagues(r.data.leagues || []);
      if (!leagueId && r.data.leagues?.[0]) setLeagueId(r.data.leagues[0].league_id);
    });
  }, []); // eslint-disable-line

  useEffect(() => {
    setLoading(true);
    if (tab === "global") {
      api.get("/news").then(r => setGlobal(r.data)).finally(() => setLoading(false));
    } else if (leagueId) {
      api.get(`/leagues/${leagueId}/my-squad-news`).then(r => setSquad(r.data)).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [tab, leagueId]);

  const items = tab === "global" ? global?.items : squad?.items;

  return (
    <div className="space-y-6 fade-up">
      <header>
        <div className="text-[var(--accent)] text-xs tracking-widest mb-1">// NEWSROOM</div>
        <h1 className="font-display text-4xl font-bold" data-testid="news-heading">NFL News & Injury Feed</h1>
      </header>

      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="inline-flex border border-[var(--border)] rounded-md overflow-hidden">
          <button onClick={() => setTab("global")} className={`px-4 py-2 text-sm inline-flex items-center gap-2 ${tab === "global" ? "bg-[var(--accent)] text-black" : "text-[var(--text-2)] hover:bg-white/5"}`} data-testid="news-tab-global">
            <Newspaper className="w-4 h-4" /> Global
          </button>
          <button onClick={() => setTab("squad")} className={`px-4 py-2 text-sm inline-flex items-center gap-2 ${tab === "squad" ? "bg-[var(--accent)] text-black" : "text-[var(--text-2)] hover:bg-white/5"}`} data-testid="news-tab-squad">
            <Users className="w-4 h-4" /> My Squad
          </button>
        </div>
        {tab === "squad" && (
          <select className="input-dark md:w-80" value={leagueId} onChange={e => setLeagueId(e.target.value)} data-testid="news-league-select">
            <option value="">— Pick a league —</option>
            {leagues.map(l => <option key={l.league_id} value={l.league_id}>{l.name}</option>)}
          </select>
        )}
      </div>

      {loading && <div className="card-bordered p-12 flex items-center justify-center"><Loader2 className="w-6 h-6 spin text-[var(--accent)]" /></div>}

      {!loading && tab === "squad" && squad?.roster_players && (
        <div className="text-xs tracking-widest text-[var(--text-3)]">
          FILTERING ACROSS {squad.roster_players.length} ROSTERED PLAYERS
        </div>
      )}

      {!loading && items && (
        <div className="space-y-2">
          {items.length === 0 && (
            <div className="card-bordered p-12 text-center text-[var(--text-2)]" data-testid="news-empty">
              {tab === "squad" ? "No recent news mentions players on your roster." : "No news yet — try again in a moment."}
            </div>
          )}
          {items.map((n, i) => (
            <a key={i} href={n.link} target="_blank" rel="noreferrer" className="card-bordered p-4 block hover:border-zinc-700 transition-colors group" data-testid={`news-item-${i}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-[10px] tracking-widest text-[var(--text-3)] flex items-center gap-2 mb-1">
                    {n.source} {n.matched_player && <span className="text-[var(--accent)]">· {n.matched_player.toUpperCase()}</span>}
                  </div>
                  <div className="font-medium group-hover:text-[var(--accent)] transition-colors">{n.title}</div>
                  {n.description && <div className="text-sm text-[var(--text-2)] mt-1 line-clamp-2">{n.description}</div>}
                </div>
                <ExternalLink className="w-4 h-4 text-[var(--text-3)] shrink-0 mt-1" />
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
